// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { CustomEmissionFactorSourceType, EmissionFactorSourceType } from "../types/EmissionFactorSourceTypes";

// GraphQL
import { Emission_Factor_Type_Enum, GetEmissionFactorSourcesQuery, GetProjectEmissionFactorCustomSourcesQuery } from "@/graphql";

interface EmissionFactorSourcesProvider {
  // Getters
  getEmissionFactorSources: () => GetEmissionFactorSourcesQuery["emission_factor_source"];
  getCustomEmissionFactorSources: () => GetProjectEmissionFactorCustomSourcesQuery["projectEmissionFactorCustom"];

  // Methods
  getHeatSupplySources: () => EmissionFactorSourceType[];
  getElectricitySupplySources: () => EmissionFactorSourceType[];
  getFuelSupplySources: () => EmissionFactorSourceType[];
  getEarthMovedSupplySources: () => EmissionFactorSourceType[];
  getHeatSupplyCustomSources: () => CustomEmissionFactorSourceType[];
  getElectricitySupplyCustomSources: () => CustomEmissionFactorSourceType[];
  getFuelSupplyCustomSources: () => CustomEmissionFactorSourceType[];
  getEmissionFactorDataSource: (id: string) => string;
  mapData: (data: GetEmissionFactorSourcesQuery) => void;
  mapCustomData: (data: GetProjectEmissionFactorCustomSourcesQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useEmissionFactorSources = defineStore("EmissionFactorSourcesProvider", (): EmissionFactorSourcesProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const emissionFactorSources = ref<GetEmissionFactorSourcesQuery["emission_factor_source"]>([]);
  const customEmissionFactorSources = ref<GetProjectEmissionFactorCustomSourcesQuery["projectEmissionFactorCustom"]>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  function getEmissionFactorSources() {
    return emissionFactorSources.value;
  }

  function getCustomEmissionFactorSources() {
    return customEmissionFactorSources.value;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setEmissionFactorSources(newEmissionFactorSources: EmissionFactorSourceType[]) {
    emissionFactorSources.value = newEmissionFactorSources;
  }

  function setCustomEmissionFactorSources(newCustomEmissionFactorSources: CustomEmissionFactorSourceType[]) {
    customEmissionFactorSources.value = newCustomEmissionFactorSources;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetEmissionFactorSourcesQuery) {
    const { emission_factor_source } = data;

    if (!emission_factor_source) return;

    // Set default data
    setEmissionFactorSources(emission_factor_source as EmissionFactorSourceType[]);
  }

  function mapCustomData(data: GetProjectEmissionFactorCustomSourcesQuery) {
    const { projectEmissionFactorCustom } = data;

    if (!projectEmissionFactorCustom) return;

    setCustomEmissionFactorSources(projectEmissionFactorCustom as CustomEmissionFactorSourceType[]);
  }

  function getHeatSupplySources() {
    // Emission factor type should be district_heating, electricity or line_gas
    const { DISTRICT_HEATING, ELECTRICITY, LINE_GAS } = Emission_Factor_Type_Enum;
    return emissionFactorSources.value.filter((source) => {
      return source.emissionFactors_aggregate.nodes.some((factor) => {
        return (
          factor.emission_factor_type === DISTRICT_HEATING ||
          factor.emission_factor_type === ELECTRICITY ||
          factor.emission_factor_type === LINE_GAS
        );
      });
    });
  }

  function getElectricitySupplySources() {
    // Emission factor type should be electricity
    const { ELECTRICITY } = Emission_Factor_Type_Enum;
    return emissionFactorSources.value.filter((source) => {
      return source.emissionFactors_aggregate.nodes.some((factor) => {
        return factor.emission_factor_type === ELECTRICITY;
      });
    });
  }

  function getFuelSupplySources() {
    // Emission factor type should be diesel
    const { DIESEL } = Emission_Factor_Type_Enum;
    return emissionFactorSources.value.filter((source) => {
      return source.emissionFactors_aggregate.nodes.some((factor) => {
        return factor.emission_factor_type === DIESEL;
      });
    });
  }

  function getEarthMovedSupplySources() {
    // Emission factor type should be earth_moved
    const { EARTH_MOVED } = Emission_Factor_Type_Enum;
    return emissionFactorSources.value.filter((source) => {
      return source.emissionFactors_aggregate.nodes.some((factor) => {
        return factor.emission_factor_type === EARTH_MOVED;
      });
    });
  }

  function getHeatSupplyCustomSources() {
    // Heat supply accepts HEATING / DISTRICT_HEATING / LINE_GAS / ELECTRICITY
    // (mirrors the supply-type list in OperationalEnergyUse.vue and the
    // SELECTABLE_TYPES exposed by CustomDataSourcesModal).
    const { HEATING, DISTRICT_HEATING, ELECTRICITY, LINE_GAS } = Emission_Factor_Type_Enum;
    return customEmissionFactorSources.value.filter((source) => {
      return (
        source.emissionFactorType === HEATING ||
        source.emissionFactorType === DISTRICT_HEATING ||
        source.emissionFactorType === ELECTRICITY ||
        source.emissionFactorType === LINE_GAS
      );
    });
  }

  function getElectricitySupplyCustomSources() {
    // Custom emission factor type should be electricity.
    const { ELECTRICITY } = Emission_Factor_Type_Enum;
    return customEmissionFactorSources.value.filter((source) => {
      return source.emissionFactorType === ELECTRICITY;
    });
  }

  function getFuelSupplyCustomSources() {
    // Mirrors the fuel-typed set passed to EmissionConsumptionChoice's
    // emission-factor-types in the A5 (Installation) machinery-fuel row.
    const { DIESEL, BIODIESEL, GASOLINE, BLACK_OIL, GAS, METHANE } = Emission_Factor_Type_Enum;
    return customEmissionFactorSources.value.filter((source) => {
      return (
        source.emissionFactorType === DIESEL ||
        source.emissionFactorType === BIODIESEL ||
        source.emissionFactorType === GASOLINE ||
        source.emissionFactorType === BLACK_OIL ||
        source.emissionFactorType === GAS ||
        source.emissionFactorType === METHANE
      );
    });
  }

  function getEmissionFactorDataSource(id: string) {
    // Return url of data source based on id
    const source = emissionFactorSources.value.find((source) => source.id === id);
    return source?.url || "";
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    // Getters
    getEmissionFactorSources,
    getCustomEmissionFactorSources,

    // Methods
    getHeatSupplySources,
    getElectricitySupplySources,
    getFuelSupplySources,
    getEarthMovedSupplySources,
    getHeatSupplyCustomSources,
    getElectricitySupplyCustomSources,
    getFuelSupplyCustomSources,
    getEmissionFactorDataSource,
    mapData,
    mapCustomData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useEmissionFactorSources;
